<form [formGroup]="form" class="flex flex-col gap-xl">
  <div>
    <lib-select
      [inputFormControlName]="'type'"
      id="custom_workflow_field_type"
      name="type"
      [required]="true"
      [options]="typeOptions()"
      [selectedKeyName]="'label'"
      [selectedKeyValue]="'value'"
      [disabled]="form.controls.type.disabled"
      [label]="'custom_workflow_field_type_label' | transloco" />
    <lib-validation-message
      [lang]="'current_lang' | transloco"
      control="type" />
  </div>

  <div>
    <div formGroupName="label">
      <lib-dual-lang-input
        [requiredGroup]="true"
        [inputFormControlName]="'label'"
        [id]="'custom_workflow_field_label'"
        type="text"
        name="label"
        [requiredEn]="false"
        [requiredAr]="false"
        [label]="'custom_workflow_field_label_label' | transloco"
        [placeholderEn]="'custom_workflow_field_label_en' | transloco"
        [placeholderAr]="'custom_workflow_field_label_ar' | transloco" />
    </div>
    <lib-validation-message
      [lang]="'current_lang' | transloco"
      control="label" />
  </div>

  <div>
    <lib-input
      [inputFormControlName]="'key'"
      [id]="'custom_workflow_field_key'"
      type="text"
      name="key"
      [required]="true"
      [disabled]="form.controls.key.disabled"
      [label]="'custom_workflow_field_key_label' | transloco"
      (typing)="markKeyTouched()" />
    <p class="text-xs text-neutral-500 mt-xs">
      {{ 'custom_workflow_field_key_hint' | transloco }}
    </p>
    <lib-validation-message [lang]="'current_lang' | transloco" control="key" />
  </div>

  <lib-toggle
    [id]="'custom_workflow_field_mandatory'"
    [formGroup]="form"
    [inputFormControlName]="'mandatory'"
    [disabled]="form.controls.mandatory.disabled || readonly()"
    [options]="mandatoryToggle()" />

  <div>
    <div formGroupName="hint">
      <lib-dual-lang-input
        [inputFormControlName]="'hint'"
        [id]="'custom_workflow_field_hint'"
        type="text"
        name="hint"
        [requiredEn]="false"
        [requiredAr]="false"
        [label]="'custom_workflow_field_hint_label' | transloco"
        [placeholderEn]="'custom_workflow_field_hint_en' | transloco"
        [placeholderAr]="'custom_workflow_field_hint_ar' | transloco" />
    </div>
    <lib-validation-message
      [lang]="'current_lang' | transloco"
      control="hint" />
  </div>

  @if (selectedType()?.has_placeholder) {
    <div>
      <div formGroupName="placeholder">
        <lib-dual-lang-input
          [inputFormControlName]="'placeholder'"
          [id]="'custom_workflow_field_placeholder'"
          type="text"
          name="placeholder"
          [requiredEn]="false"
          [requiredAr]="false"
          [label]="'custom_workflow_field_placeholder_label' | transloco"
          [placeholderEn]="'custom_workflow_field_placeholder_en' | transloco"
          [placeholderAr]="
            'custom_workflow_field_placeholder_ar' | transloco
          " />
      </div>
      <lib-validation-message
        [lang]="'current_lang' | transloco"
        control="placeholder" />
    </div>
  }

  @if (selectedType()?.has_options) {
    <div formArrayName="options" class="flex flex-col gap-md">
      <span class="text-sm font-medium text-neutral-700">{{
        'custom_workflow_field_options_label' | transloco
      }}</span>
      @for (row of optionRows(); track $index; let i = $index) {
        <div
          [formGroupName]="i"
          class="grid grid-cols-[1fr_2fr_auto] gap-sm items-end">
          <lib-input
            [inputFormControlName]="'value'"
            [id]="'custom_workflow_option_value_' + i"
            type="text"
            name="value"
            [disabled]="!row.removable"
            [placeholder]="
              'custom_workflow_option_value_placeholder' | transloco
            " />
          <div>
            <div formGroupName="label">
              <lib-dual-lang-input
                [requiredGroup]="true"
                [inputFormControlName]="'label'"
                [id]="'custom_workflow_option_label_' + i"
                type="text"
                name="label"
                [requiredEn]="false"
                [requiredAr]="false"
                [placeholderEn]="
                  'custom_workflow_option_en_placeholder' | transloco
                "
                [placeholderAr]="
                  'custom_workflow_option_ar_placeholder' | transloco
                " />
            </div>
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              control="label" />
          </div>
          @if (row.removable && !readonly()) {
            <button
              type="button"
              [id]="'custom_workflow_remove_option_' + i"
              class="text-destructive-600 px-sm py-sm"
              [attr.aria-label]="
                'custom_workflow_remove_option_label' | transloco
              "
              (click)="removeOption(i)">
              ✕
            </button>
          } @else {
            <span></span>
          }
        </div>
      }
      @if (!readonly()) {
        <button
          type="button"
          id="custom_workflow_add_option"
          class="self-start text-sm font-medium text-primary-600"
          (click)="addOption()">
          + {{ 'custom_workflow_add_option_button' | transloco }}
        </button>
      }
      <lib-validation-message
        [lang]="'current_lang' | transloco"
        control="options" />
    </div>
  }

  @if (selectedType()?.is_media) {
    <div>
      <lib-select
        [inputFormControlName]="'attachment_type'"
        id="custom_workflow_field_attachment_type"
        name="attachment_type"
        [required]="true"
        [options]="attachmentTypeOptions()"
        [selectedKeyName]="'label'"
        [selectedKeyValue]="'value'"
        [label]="'custom_workflow_field_attachment_type_label' | transloco" />
      <lib-validation-message
        [lang]="'current_lang' | transloco"
        control="attachment_type" />
    </div>
    <div class="grid grid-cols-2 gap-lg">
      <div>
        <lib-multi-select
          [inputFormControlName]="'extensions'"
          [id]="'custom_workflow_field_extensions'"
          [name]="'extensions'"
          [required]="true"
          [options]="extensionOptions()"
          [selectedKeyName]="'label'"
          [selectedKeyValue]="'value'"
          [label]="'custom_workflow_field_extensions_label' | transloco" />
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="extensions" />
      </div>
      <div>
        <lib-input
          [inputFormControlName]="'max_file_size'"
          [id]="'custom_workflow_field_max_size'"
          type="number"
          name="max_file_size"
          [required]="true"
          [label]="'custom_workflow_field_max_size_label' | transloco" />
        @if (maxFileSizeLimit(); as limit) {
          <p class="text-xs text-neutral-500 mt-xs">
            {{
              'custom_workflow_field_max_size_hint' | transloco: { max: limit }
            }}
          </p>
        }
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="max_file_size" />
      </div>
    </div>
  }

  @if (
    selectedType()?.value === inputTypesEnum.VARIABLE_OPTIONS ||
    selectedType()?.value === inputTypesEnum.LOOKUP
  ) {
    <div>
      <lib-select
        [inputFormControlName]="'lookup_model_config_id'"
        id="custom_workflow_field_lookup_model"
        name="lookup_model_config_id"
        [required]="true"
        [disabled]="form.controls.lookup_model_config_id.disabled"
        [options]="lookupOptions()"
        [selectedKeyName]="'label'"
        [selectedKeyValue]="'value'"
        [label]="'custom_workflow_field_lookup_label' | transloco" />
      <lib-validation-message
        [lang]="'current_lang' | transloco"
        control="lookup_model_config_id" />
    </div>
  }

  <div class="flex justify-end gap-md border-t border-neutral-200 pt-lg">
    <lib-button
      id="custom_workflow_field_cancel"
      [label]="'custom_workflow_cancel_button' | transloco"
      [btnClass]="'px-xl py-sm text-sm rounded-md text-neutral-700 bg-white font-medium border border-neutral-300'"
      (click)="cancelled.emit()" />
    @if (!readonly()) {
      <lib-button
        id="custom_workflow_field_save"
        [disabled]="isSaving()"
        [label]="'custom_workflow_save_button' | transloco"
        [btnClass]="'px-xl py-sm text-sm rounded-md text-white bg-primary-500 font-medium border-none'"
        (click)="submit()" />
    }
  </div>
</form>
